import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@watt/ui/components/ui/accordion";
import { Button } from "@watt/ui/components/ui/button";
import { FormField } from "@watt/ui/components/ui/form";
import { Separator } from "@watt/ui/components/ui/separator";
import { Plus, Trash } from "lucide-react";
import { useFieldArray, type useForm, useFormContext } from "react-hook-form";
import * as z from "zod";
import { DEFAULT_ZOD_HANDLERS, INPUT_COMPONENTS } from "../utils/config";
import resolveDependencies from "../utils/dependencies";
import type { Dependency, FieldConfig, FieldConfigItem } from "../utils/types";
import {
  beautifyObjectName,
  getBaseSchema,
  getBaseType,
  zodToHtmlInputProps
} from "../utils/utils";

// Helper functions for AutoFormArray
function isZodArray(
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
  item: z.ZodArray<any> | z.ZodDefault<any>
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
): item is z.ZodArray<any> {
  return item instanceof z.ZodArray;
}

function isZodDefault(
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
  item: z.ZodArray<any> | z.ZodDefault<any>
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
): item is z.ZodDefault<any> {
  return item instanceof z.ZodDefault;
}

// Helper component for AutoFormObject
function DefaultParent({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function AutoFormArray({
  name,
  item,
  form,
  path = [],
  fieldConfig
}: {
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
  item: z.ZodArray<any> | z.ZodDefault<any>;
  form: ReturnType<typeof useForm>;
  path?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
  fieldConfig?: any;
}) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name
  });
  const title = item._def.description ?? beautifyObjectName(name);

  const itemDefType = isZodArray(item)
    ? item._def.type
    : isZodDefault(item)
      ? item._def.innerType._def.type
      : null;

  return (
    <AccordionItem className="border-none" value={name}>
      <AccordionTrigger>{title}</AccordionTrigger>
      <AccordionContent>
        {fields.map((_field, index) => {
          const key = _field.id;
          return (
            <div className="mt-4 flex flex-col" key={`${key}`}>
              <AutoFormObject
                fieldConfig={fieldConfig}
                form={form}
                path={[...path, index.toString()]}
                // biome-ignore lint/suspicious/noExplicitAny: <fix later>
                schema={itemDefType as z.ZodObject<any, any>}
              />
              <div className="my-4 flex justify-end">
                <Button
                  className="hover:bg-zinc-300 hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-white dark:text-black dark:focus-visible:ring-0 dark:focus-visible:ring-offset-0 dark:hover:bg-zinc-300 dark:hover:text-black dark:hover:ring-0 dark:hover:ring-offset-0"
                  onClick={() => remove(index)}
                  size="icon"
                  type="button"
                  variant="secondary"
                >
                  <Trash className="size-4 " />
                </Button>
              </div>

              <Separator />
            </div>
          );
        })}
        <Button
          className="mt-4 flex items-center"
          onClick={() => append({})}
          type="button"
          variant="secondary"
        >
          <Plus className="mr-2" size={16} />
          Add
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
}

export function AutoFormObject<
  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
  SchemaType extends z.ZodObject<any, any>
>({
  schema,
  form,
  fieldConfig,
  path = [],
  dependencies = []
}: {
  schema: SchemaType | z.ZodEffects<SchemaType>;
  form: ReturnType<typeof useForm>;
  fieldConfig?: FieldConfig<z.infer<SchemaType>>;
  path?: string[];
  dependencies?: Dependency<z.infer<SchemaType>>[];
}) {
  const { watch } = useFormContext(); // Use useFormContext to access the watch function

  if (!schema) {
    return null;
  }
  const { shape } = getBaseSchema<SchemaType>(schema) || {};

  if (!shape) {
    return null;
  }

  const handleIfZodNumber = (item: z.ZodAny) => {
    // biome-ignore lint/suspicious/noExplicitAny: <fix later>
    const isZodNumber = (item as any)._def.typeName === "ZodNumber";
    const isInnerZodNumber =
      // biome-ignore lint/suspicious/noExplicitAny: <fix later>
      (item._def as any).innerType?._def?.typeName === "ZodNumber";

    if (isZodNumber) {
      // biome-ignore lint/suspicious/noExplicitAny: <fix later>
      (item as any)._def.coerce = true;
    } else if (isInnerZodNumber) {
      // biome-ignore lint/suspicious/noExplicitAny: <fix later>
      (item._def as any).innerType._def.coerce = true;
    }

    return item;
  };

  return (
    <Accordion className="space-y-5 border-none" type="multiple">
      {Object.keys(shape).map(name => {
        let item = shape[name] as z.ZodAny;
        item = handleIfZodNumber(item) as z.ZodAny;
        const zodBaseType = getBaseType(item);
        const itemName = item._def.description ?? beautifyObjectName(name);
        const key = [...path, name].join(".");

        const {
          isHidden,
          isDisabled,
          isRequired: isRequiredByDependency,
          overrideOptions
        } = resolveDependencies(dependencies, name, watch);
        if (isHidden) {
          return null;
        }

        if (zodBaseType === "ZodObject") {
          return (
            <AccordionItem className="border-none" key={key} value={name}>
              <AccordionTrigger>{itemName}</AccordionTrigger>
              <AccordionContent className="p-2">
                <AutoFormObject
                  fieldConfig={
                    (fieldConfig?.[name] ?? {}) as FieldConfig<
                      z.infer<typeof item>
                    >
                  }
                  form={form}
                  path={[...path, name]}
                  // biome-ignore lint/suspicious/noExplicitAny: <fix later>
                  schema={item as unknown as z.ZodObject<any, any>}
                />
              </AccordionContent>
            </AccordionItem>
          );
        }
        if (zodBaseType === "ZodArray") {
          return (
            <AutoFormArray
              fieldConfig={fieldConfig?.[name] ?? {}}
              form={form}
              // biome-ignore lint/suspicious/noExplicitAny: <fix later>
              item={item as unknown as z.ZodArray<any>}
              key={key}
              name={name}
              path={[...path, name]}
            />
          );
        }

        const fieldConfigItem: FieldConfigItem = fieldConfig?.[name] ?? {};
        const zodInputProps = zodToHtmlInputProps(item);
        const isRequired =
          isRequiredByDependency ||
          zodInputProps.required ||
          fieldConfigItem.inputProps?.required ||
          false;

        if (overrideOptions) {
          item = z.enum(overrideOptions) as unknown as z.ZodAny;
        }

        return (
          <FormField
            // biome-ignore lint/suspicious/noExplicitAny: <fix later>
            control={form.control as unknown as any}
            key={key}
            name={key}
            render={({ field }) => {
              const inputType =
                fieldConfigItem.fieldType ??
                DEFAULT_ZOD_HANDLERS[zodBaseType] ??
                "fallback";

              const InputComponent =
                typeof inputType === "function"
                  ? inputType
                  : INPUT_COMPONENTS[inputType];

              const ParentElement =
                fieldConfigItem.renderParent ?? DefaultParent;

              const defaultValue = fieldConfigItem.inputProps?.defaultValue;
              const value = field.value ?? defaultValue ?? "";

              const fieldProps = {
                ...zodToHtmlInputProps(item),
                ...field,
                ...fieldConfigItem.inputProps,
                disabled: fieldConfigItem.inputProps?.disabled || isDisabled,
                ref: undefined,
                value: value
              };

              if (InputComponent === undefined) {
                return <span />;
              }

              return (
                <ParentElement key={`${key}.parent`}>
                  <InputComponent
                    className={fieldProps.className}
                    field={field}
                    fieldConfigItem={fieldConfigItem}
                    fieldProps={fieldProps}
                    isRequired={isRequired}
                    label={itemName}
                    zodInputProps={zodInputProps}
                    zodItem={item}
                  />
                </ParentElement>
              );
            }}
          />
        );
      })}
    </Accordion>
  );
}
